/**
 * ItemRationaleCell — the per-item "why" on a results row (Brief K slice 1;
 * Doc 21 §6.4 rationale notes). Collapsed to a count; expands to the recorded
 * lines and a one-line input. Optional by canon: never blocks anything.
 */
import { useMemo, useState } from 'react';
import { MessageSquareText, Loader2 } from 'lucide-react';
import { useDeliberationStore } from '../../../stores/prioritization/deliberationStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { NO_RATIONALE_RECORDED, rationaleForItem } from '../../../lib/deliberationCore';
import { ReverseActionButton } from '../../reversals/ReverseActionButton';
import { Button } from '../../ui/Button';

interface ItemRationaleCellProps {
  sessionId: string;
  workspaceId: string;
  itemId: string;
  canWrite: boolean;
}

export function ItemRationaleCell({ sessionId, workspaceId, itemId, canWrite }: ItemRationaleCellProps) {
  const annotations = useDeliberationStore((s) => s.annotations);
  const overrides = useDeliberationStore((s) => s.overrides);
  const addAnnotation = useDeliberationStore((s) => s.addAnnotation);
  const editAnnotation = useDeliberationStore((s) => s.editAnnotation);
  const retractAnnotation = useDeliberationStore((s) => s.retractAnnotation);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const userId = useUserStore((s) => s.userId);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const lines = useMemo(
    () => rationaleForItem(itemId, annotations.filter((a) => a.sessionId === sessionId), overrides.filter((o) => o.sessionId === sessionId)),
    [itemId, annotations, overrides, sessionId],
  );

  const save = async () => {
    if (!userId) return;
    setBusy(true);
    setError('');
    const res = await addAnnotation({ sessionId, workspaceId, itemId, authorId: userId, content: draft });
    setBusy(false);
    if (!res.ok) {
      setError(res.error ?? 'Could not save.');
      return;
    }
    setDraft('');
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setBusy(true);
    setError('');
    const res = await editAnnotation(editingId, editDraft);
    setBusy(false);
    if (!res.ok) {
      setError(res.error ?? 'Could not save the edit.');
      return;
    }
    setEditingId(null);
  };

  return (
    <div className="text-left">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1 text-xs ${lines.length > 0 ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'} hover:underline`}
        title={lines.length > 0 ? `${lines.length} recorded reason(s)` : 'Record why'}
      >
        <MessageSquareText className="h-3.5 w-3.5" />
        {lines.length > 0 ? lines.length : canWrite ? 'why?' : '—'}
      </button>
      {open && (
        <div className="mt-2 space-y-2 min-w-[16rem] max-w-md">
          {lines.length === 0 ? (
            <p className="text-xs text-[var(--text-ter)] italic">{NO_RATIONALE_RECORDED}</p>
          ) : (
            <ul className="space-y-1">
              {lines.map((l, i) => (
                <li key={`${l.kind}-${l.createdAt}-${i}`} className="text-xs text-[var(--text-sec)]">
                  <span className={`mr-1 font-medium ${l.kind === 'override' ? 'text-[var(--mustard)]' : 'text-[var(--text-ter)]'}`}>
                    {l.kind === 'override' ? 'Override:' : 'Note:'}
                  </span>
                  {editingId && editingId === l.annotationId ? (
                    <span className="inline-flex items-center gap-1">
                      <input
                        type="text"
                        value={editDraft}
                        onChange={(e) => setEditDraft(e.target.value)}
                        className="border border-[var(--rule)] bg-[var(--bg-raised)] px-1 py-0.5 text-xs text-[var(--text-pri)] outline-none focus:border-[var(--slate)]"
                        disabled={busy}
                        aria-label="Edit your note"
                      />
                      <Button variant="ghost" size="sm" onClick={() => void saveEdit()} disabled={busy || !editDraft.trim()} className="!px-1 !py-0 text-xs">
                        Save
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setEditingId(null)} disabled={busy} className="!px-1 !py-0 text-xs">
                        Cancel
                      </Button>
                    </span>
                  ) : (
                    l.text
                  )}
                  {/* The author's own note: edit it, or retract it (kept, hidden — mig 746). */}
                  {l.kind === 'annotation' && l.annotationId && userId && l.authorId === userId && editingId !== l.annotationId && (
                    <span className="ml-2 inline-flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingId(l.annotationId ?? null);
                          setEditDraft(l.text);
                          setError('');
                        }}
                        className="!px-1 !py-0 text-xs"
                      >
                        Edit
                      </Button>
                      <ReverseActionButton
                        label="Retract"
                        confirmTitle="Retract your note"
                        explain="The note is kept in the session record with your reason, but it no longer shows here or in new reports. Reports already written keep what they captured."
                        run={(reason) => retractAnnotation(l.annotationId!, reason)}
                        variant="ghost"
                        className="!px-1 !py-0 text-xs"
                      />
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
          {canWrite && (
            <div className="flex items-start gap-2">
              <input
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void save();
                }}
                placeholder="Why did this land here? (⌘↵ to save)"
                className="flex-1 border border-[var(--rule)] bg-[var(--bg-raised)] px-2 py-1 text-xs text-[var(--text-pri)] outline-none focus:border-[var(--slate)]"
                disabled={busy}
              />
              <button
                type="button"
                onClick={() => void save()}
                disabled={busy || !draft.trim()}
                className="px-2 py-1 text-xs font-medium text-white bg-[var(--slate)] disabled:opacity-50"
              >
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
              </button>
            </div>
          )}
          {error && <p className="text-xs text-[var(--coral)]">{error}</p>}
        </div>
      )}
    </div>
  );
}
